/**
 * Login Page
 *
 * Simple email/password login for Firebase Auth.
 * In dev mode (VITE_DEV_AUTH_BYPASS=true), shows pre-filled credentials.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, AlertCircle, Lock, Code } from 'lucide-react';

// Dev credentials from env
const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
const DEV_EMAIL = import.meta.env.VITE_DEV_AUTH_EMAIL || '';
const DEV_PASSWORD = import.meta.env.VITE_DEV_AUTH_PASSWORD || '';

export function LoginPage() {
  const { signIn, loading, error, clearError } = useAuth();
  const [email, setEmail] = useState(DEV_AUTH_BYPASS ? DEV_EMAIL : '');
  const [password, setPassword] = useState(DEV_AUTH_BYPASS ? DEV_PASSWORD : '');

  // Auto-submit in dev mode after a brief delay
  useEffect(() => {
    if (DEV_AUTH_BYPASS && DEV_EMAIL && DEV_PASSWORD) {
      const timer = setTimeout(() => {
        signIn(DEV_EMAIL, DEV_PASSWORD).catch(() => {});
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [signIn]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      await signIn(email, password);
    } catch {
      // Error is handled by context
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-navy-900 text-brand-teal mb-4">
            <Lock size={28} />
          </div>
          <h1 className="text-2xl font-bold text-navy-900">NeuroGraph Admin</h1>
          <p className="text-sm text-slate-500 mt-1">Sign in to continue</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message */}
            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
                <AlertCircle size={16} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                autoFocus
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-navy-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal transition-all"
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-navy-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal transition-all"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full bg-navy-900 text-white font-bold py-3 px-4 rounded-xl hover:bg-navy-800 focus:outline-none focus:ring-2 focus:ring-navy-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          NeuroGraph Platform &middot; Admin Console
        </p>

        {/* Dev Mode Indicator */}
        {DEV_AUTH_BYPASS && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2">
            <Code size={14} className="text-amber-600 flex-shrink-0" />
            <div className="text-xs text-amber-700">
              <span className="font-bold">Dev Mode</span> — Auto-login enabled with local credentials
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
